// Route table (ADR 0021). Every authenticated route lives inside
// <AuthGuard /> which bounces to / if there's no session token.

import type { JSX } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './auth/AuthContext.js';
import { LoginPage } from './routes/LoginPage.js';
import { AuthCallbackPage } from './routes/AuthCallbackPage.js';
import { HomePage } from './routes/HomePage.js';
import { MenuListPage } from './routes/MenuListPage.js';
import { MenuEditorPage } from './routes/MenuEditorPage.js';
import { OutletSettingsPage } from './routes/OutletSettingsPage.js';
import { FeedbackInboxPage } from './routes/FeedbackInboxPage.js';
import { LoyaltyPage } from './routes/LoyaltyPage.js';
import { AnalyticsPage } from './routes/AnalyticsPage.js';
import { PosPage } from './routes/PosPage.js';
import { GuestOrdersPage } from './routes/GuestOrdersPage.js';
import { TablesPage } from './routes/TablesPage.js';
import { PosSettlementsPage } from './routes/PosSettlementsPage.js';
import { OpsPage } from './routes/OpsPage.js';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

function AuthGuard(): JSX.Element {
  const { token } = useAuth();
  if (!token) return <Navigate to="/" replace />;
  return <Outlet />;
}

function NotFoundPage(): JSX.Element {
  return (
    <main className="page">
      <h1>Not found</h1>
      <p className="muted">Nothing lives at this URL.</p>
    </main>
  );
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route element={<AuthGuard />}>
              <Route path="/home" element={<HomePage />} />
              <Route path="/ops" element={<OpsPage />} />
              <Route path="/orgs/:orgId/outlets/:outletId/menus" element={<MenuListPage />} />
              <Route
                path="/orgs/:orgId/outlets/:outletId/settings"
                element={<OutletSettingsPage />}
              />
              <Route path="/orgs/:orgId/menus/:menuId" element={<MenuEditorPage />} />
              <Route
                path="/orgs/:orgId/outlets/:outletId/feedback"
                element={<FeedbackInboxPage />}
              />
              <Route
                path="/orgs/:orgId/restaurants/:restaurantId/loyalty"
                element={<LoyaltyPage />}
              />
              <Route path="/orgs/:orgId/outlets/:outletId/analytics" element={<AnalyticsPage />} />
              <Route path="/orgs/:orgId/outlets/:outletId/pos" element={<PosPage />} />
              <Route path="/orgs/:orgId/outlets/:outletId/tables" element={<TablesPage />} />
              <Route
                path="/orgs/:orgId/outlets/:outletId/guest-orders"
                element={<GuestOrdersPage />}
              />
              <Route
                path="/orgs/:orgId/restaurants/:restaurantId/pos/settlements"
                element={<PosSettlementsPage />}
              />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
